import { createEffect, untrack } from 'solid-js';

type ProgressArcProps = {
    /** Total animation duration in ms — typically the auto-advance interval. */
    durationMs: number;
    /** Changing this restarts the animation (e.g. on slide change). */
    resetKey: number;
    /** Accessor returning whether the arc should be running. */
    isPlaying: () => boolean;
};

/**
 * Auto-advance progress arc — overlays the play/pause button and sweeps a stroke
 * clockwise around it as `durationMs` elapses. Restarts whenever `resetKey`
 * changes (i.e. on slide change); pauses/resumes via `isPlaying`.
 *
 * Geometry mirrors the DS sm-variant VideoControl (32x32 viewBox, 2px stroke,
 * outer edge on the perimeter). The animated stroke uses `currentColor`, so place
 * inside an element with the desired text-color class. The dasharray endpoint
 * (94.248) is the circumference of r=15.
 */
export const ProgressArc = (props: ProgressArcProps) => {
    let circleRef: SVGCircleElement | undefined;

    // Restart on slide change. isPlaying is read via untrack so this effect only re-runs on
    // resetKey changes; pause/resume is the second effect's job.
    createEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ = props.resetKey;
        if (!circleRef) return;
        const anims = circleRef.getAnimations();
        anims.forEach((anim) => {
            anim.cancel();
            anim.play();
        });
        if (!untrack(() => props.isPlaying())) {
            anims.forEach((anim) => anim.pause());
        }
    });

    // Pause/resume via the Web Animations API rather than CSS `animation-play-state` —
    // Solid's reactive style binding is unreliable inside SVG style attributes.
    createEffect(() => {
        const playing = props.isPlaying();
        if (!circleRef) return;
        circleRef.getAnimations().forEach((anim) => {
            if (playing) anim.play();
            else anim.pause();
        });
    });

    return (
        <svg
            class="absolute inset-0 size-full -rotate-90 pointer-events-none"
            viewBox="0 0 32 32"
            aria-hidden="true"
        >
            {/* Static track — currentColor (cvi-fg-default) at 20% opacity, matching the DS asset. */}
            <circle
                cx="16"
                cy="16"
                r="15"
                fill="none"
                stroke="currentColor"
                stroke-opacity="0.2"
                stroke-width="2"
            />
            {/* Animated arc — same geometry, fg-default at full opacity; dashoffset animates 94.248 → 0. */}
            <circle
                ref={circleRef}
                cx="16"
                cy="16"
                r="15"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-dasharray="94.248"
                stroke-dashoffset="94.248"
                style={{
                    animation: `cvi-hero-progress-arc ${props.durationMs}ms linear forwards`,
                }}
            />
        </svg>
    );
};
